import { Alert, Box, Link } from '@mui/material';
import { BaseRow } from '~/components/common';
import ProjectSummary from '~/components/dashboard/project-summary/ProjectSummary';
import ProjectTimeline from '~/components/dashboard/project-timeline/ProjectTimeline';
import Card from './components/Card';
import {
  CalendarIcon,
  HierarchyIcon,
  LoadingIcon,
  NotificationStatusIcon,
  TickCircleIcon,
  UserTickIcon,
  UsersIcon,
} from '~/assets/icons';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { invoiceActions } from '~/thunks/invoice/invoiceSlice';
import dayjs from 'dayjs';
import { STATUS_OPEN } from '~/utils/constants/invoice';
import { adminRouteAbsolute } from '~/utils/constants/route';
import { useNavigate } from 'react-router-dom';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { useEffect, useState } from 'react';
import { handleGetDashboard } from '~/thunks/report/reportThunk';
import { formatCurrency, handleApiError } from '~/utils/helper';
import { IDashboard } from '~/utils/interface/report';

const Dashboard = () => {
  //#region Declare Hook
  const { t } = useTranslation('admin/dashboard');
  //#endregion Declare Hook

  //#region Selector

  const { profile } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const permission = usePermission();
  const [dashboard, setDashboard] = useState<IDashboard>();

  //#endregion Selector

  //#region Handle Function
  const handleClickExpiredInvoiceDetail = () => {
    dispatch(
      invoiceActions.setFilter({
        expireDateTo: dayjs().toISOString(),
        status: STATUS_OPEN,
        sort: [
          {
            field: 'expireDate',
            type: 'ASC',
          },
        ],
      })
    );
    navigate(adminRouteAbsolute.invoices);
  };
  useEffect(() => {
    dispatch(handleGetDashboard())
      .unwrap()
      .then((response) => {
        setDashboard(response);
      })
      .catch((e) => {
        handleApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  //#endregion Handle Function
  return (
    <>
      {permission[PERMISSIONS.invoice_reminder] && (profile?.countExpiredInvoices ?? 0) > 0 && (
        <Alert severity='error' className='mb-[20px]'>
          <Link onClick={handleClickExpiredInvoiceDetail} className='underline text-[#d52b1e] text-[14px]'>
            {t('alertExpiredInvoice', {
              countExpiredInvoices: profile?.countExpiredInvoices,
            })}
          </Link>
        </Alert>
      )}
      <Box className='car-statistic grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[30px]'>
        {/* Using localization */}
        <Card
          title={t('totalProject')}
          content={dashboard?.jobThisMonth ?? ''}
          percent={Math.abs(Number(dashboard?.jobPercent) ?? 0)}
          icon={<UsersIcon className='[&>path]:stroke-[#0D062D]' />}
          type={dashboard?.jobPercent && dashboard.jobPercent > 0 ? 'up' : 'down'}
          onClick={() => navigate(adminRouteAbsolute.projectReport)}
        />
        <Card
          title={t('estimatedSales')}
          content={formatCurrency(dashboard?.estimateThisMonth ?? '')}
          percent={Math.abs(Number(dashboard?.estimatePercent) || 0)}
          icon={<UserTickIcon />}
          type={dashboard?.estimatePercent && dashboard.estimatePercent > 0 ? 'up' : 'down'}
          onClick={() => navigate(adminRouteAbsolute.staffSaleReport)}
        />
        <Card
          title={t('sales')}
          content={formatCurrency(dashboard?.invoiceThisMonth ?? '')}
          percent={Math.abs(Number(dashboard?.invoicePercent) || 0)}
          icon={<HierarchyIcon />}
          type={dashboard?.invoicePercent && dashboard.invoicePercent > 0 ? 'up' : 'down'}
          onClick={() => navigate(adminRouteAbsolute.saleReport)}
        />
        <Card
          title={t('crmActivities')}
          content={dashboard?.activityThisMonth ?? ''}
          percent={Math.abs(Number(dashboard?.activityPercent || 0))}
          icon={<NotificationStatusIcon />}
          type={dashboard?.activityPercent && dashboard.activityPercent > 0 ? 'up' : 'down'}
          onClick={() => navigate(adminRouteAbsolute.activityReport)}
        />
      </Box>

      <BaseRow className='mt-[30px] mb-[30px] gap-[30px]'>
        <Box className='lg:w-[100%]'>
          <ProjectTimeline />
        </Box>
        <Box hidden={true} className='lg:w-[30%]'>
          <Box className='text-[#040C2C] text-[18px]/[31px] font-medium '>{t('projectStats')}</Box>
          <Box className='mt-[17px] p-[20px] border-[1px] border-[#E5EAEF] rounded-[8px]'>
            {/* items */}
            <Box className='h-[63px]'>
              <Box className='flex justify-between items-center'>
                <Box className='flex items-center gap-[12px]'>
                  <Box className='w-[30px] h-[30px] rounded-full bg-[#FEE4E4] flex items-center justify-center'>
                    <CalendarIcon className='h-[15px] w-[15px] [&>path]:stroke-[#FF0000]' />
                  </Box>
                  <span className='text-[16px]/[19.2px] text-[#495275] font-medium'>{t('upComingProjects')}</span>
                </Box>
                <Box className='text-[16px]/[19.2px] text-[#59607C] font-semibold'>00</Box>
              </Box>
              <Box className='mt-[12px] font-normal text-[14px]/[16.8px] text-[#606060]'>
                <span className='font-semibold text-[#4AD991]'>+0%</span> {t('moreFromLastWeek')}
              </Box>
            </Box>

            <Box className=' pb-[16px] border-b-[1px] border-b-[#E5EAEF] mb-[16px]' />

            <Box className='h-[63px]'>
              <Box className='flex justify-between items-center'>
                <Box className='flex items-center gap-[12px]'>
                  <Box className='w-[30px] h-[30px] rounded-full bg-[#E4F2FE] flex items-center justify-center'>
                    <LoadingIcon className='h-[15px] w-[15px] [&>path]:stroke-[#0076DB]' />
                  </Box>
                  <span className='text-[16px]/[19.2px] text-[#495275] font-medium'>{t('ongoingProjects')}</span>
                </Box>
                <Box className='text-[16px]/[19.2px] text-[#59607C] font-semibold'>00</Box>
              </Box>
              <Box className='mt-[12px] font-normal text-[14px]/[16.8px] text-[#606060]'>
                <span className='font-semibold text-[#4AD991]'>+0%</span> {t('moreFromLastWeek')}
              </Box>
            </Box>

            <Box className=' pb-[16px] border-b-[1px] border-b-[#E5EAEF] mb-[16px]' />

            <Box className='h-[63px]'>
              <Box className='flex justify-between items-center'>
                <Box className='flex items-center gap-[12px]'>
                  <Box className='w-[30px] h-[30px] rounded-full bg-[#E0FFF0] flex items-center justify-center'>
                    <TickCircleIcon className='h-[15px] w-[15px] [&>path]:stroke-[#008A48]' />
                  </Box>
                  <span className='text-[16px]/[19.2px] text-[#495275] font-medium'>{t('completedProjects')}</span>
                </Box>
                <Box className='text-[16px]/[19.2px] text-[#59607C] font-semibold'>00</Box>
              </Box>
              <Box className='mt-[12px] font-normal text-[14px]/[16.8px] text-[#606060]'>
                <span className='font-semibold text-[#4AD991]'>+0%</span> {t('moreFromLastWeek')}
              </Box>
            </Box>
          </Box>
        </Box>
      </BaseRow>

      <ProjectSummary />
    </>
  );
};

export default Dashboard;
